'use client';

import { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { ProjectCard } from './ProjectCard';
import { ProjectForm } from './ProjectForm';
import { useProjectStore } from '@/stores/project-store';
import { toast } from 'sonner';
import type { Project } from "dico-types";

interface ProjectListProps {
  workspaceId: string;
}

export function ProjectList({ workspaceId }: ProjectListProps) {
  const projectsByWorkspaceId = useProjectStore((s) => s.projectsByWorkspaceId);
  const projects = useMemo(() => projectsByWorkspaceId[workspaceId] ?? [], [projectsByWorkspaceId, workspaceId]);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const createProject = useProjectStore((s) => s.createProject);
  const updateProject = useProjectStore((s) => s.updateProject);
  const deleteProject = useProjectStore((s) => s.deleteProject);

  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await fetchProjects(workspaceId);
      } catch (error) {
        console.error('Failed to load projects:', error);
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, fetchProjects]);

  const handleCreate = () => {
    setEditingProject(null);
    setFormOpen(true);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormOpen(true);
  };

  const handleDeleteClick = (project: Project) => {
    setDeleteId(project.id);
  };

  const handleSave = async (data: {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
  }) => {
    try {
      if (editingProject) {
        await updateProject(workspaceId, editingProject.id, data);
        toast.success('Project updated');
      } else {
        await createProject(workspaceId, data);
        toast.success('Project created');
      }
    } catch (error) {
      console.error('Failed to save project:', error);
      toast.error(editingProject ? 'Failed to update project' : 'Failed to create project');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteProject(workspaceId, deleteId);
      toast.success('Project deleted');
      setDeleteId(null);
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <MonoIcon type="Loader2" className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Projects</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Organize your work into projects. Resources like tags, statuses, and tickets can be
            scoped to specific projects.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <MonoIcon type="Plus" className="mr-2" />
          New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-muted-foreground mb-4">No projects yet</p>
          <Button onClick={handleCreate} variant="outline">
            <MonoIcon type="Plus" className="mr-2" />
            Create your first project
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      <ProjectForm
        open={formOpen}
        onOpenChange={setFormOpen}
        workspaceId={workspaceId}
        project={editingProject}
        onSave={handleSave}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the project. All resources (tags, statuses, priorities, tickets)
              associated with this project will become global (workspace-level).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
